import React, { Component } from 'react';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
// import isEmpty from 'lodash/isEmpty';
// Local Deps
import errorMsgs from '../../config/errors';
import {
  // addMatchSettings,
  // deleteMatchSettings,
  updateMatchSettings,
} from '../../graphql/matchSettings';
import { requestUserById } from '../../graphql/users';
import {
  configureFilterGroups,
  addFilterToComplexObject,
  removeFilterFromComplexObject,
} from '../../utils/filterGroupsUtils';
import { debounce } from '../../utils/debounceUtils';
import materialFilters from '../../config/searchFilters/materialFilters';
import methodFilters from '../../config/searchFilters/methodFilters';
import { formatMethodValuesArray, displayToEnumFormat } from '../../utils/methodFormatter';

export default function BidMatchSettingsHOC(BidMatchSettingsPage) {
  class WrappedComponent extends Component {
    static initialState = {
      cardList: [],
      errors: [],
      isLoading: true,
      uiMessage: {},
    };

    static propTypes = {
      auth: PropTypes.shape({
        id: PropTypes.string,
        role: PropTypes.string,
        expiration: PropTypes.number,
        organizationId: PropTypes.string,
      }).isRequired,
    };

    static _transformMatchSettings(matchSettingsList) {
      // Static filter groups use singular groupNames instead of the plural
      // ones that are returned from the server
      const formattedMatchSettings = matchSettingsList.map(ms => {
        // Format values from server
        const material = ms.materials
          ? ms.materials.map(mat => startCase(mat))
          : [];
        // Use method formatter to preserve slashes and handle camelCase IDs
        const method = ms.methods ? formatMethodValuesArray(ms.methods) : [];
        const minimumQuantity = ms.minimumQuantity
          ? parseFloat(ms.minimumQuantity)
          : 0;
        const minimumEstimatedCost = ms.minimumEstimatedCost
          ? parseFloat(ms.minimumEstimatedCost)
          : 0;
        return {
          material,
          method,
          minimumQuantity,
          minimumEstimatedCost,
        };
      });
      return formattedMatchSettings;
    }

    state = WrappedComponent.initialState;

    async componentDidMount() {
      this._fetchMatchSettings();
    }

    _fetchMatchSettings = async () => {
      const { organizationId: userId } = this.props.auth;
      const response = await requestUserById(userId);
      let matchSettingList;
      if (response.organization.matchSettingList) {
        matchSettingList = [response.organization.matchSettingList];
      } else {
        matchSettingList = [];
      }
      // Format settings for state
      const formattedMatchSettings = WrappedComponent._transformMatchSettings(
        matchSettingList,
      );
      if (formattedMatchSettings[0]) {
        this.setState({
          isLoading: false,
          cardList: formattedMatchSettings[0],
        });
      } else {
        this.setState({
          isLoading: false,
          cardList: formattedMatchSettings,
        });
      }
      // if (isEmpty(formattedMatchSettings)) {
      //   this._onAddCardClick();
      // }
    };

    _onFilterClick = async (filter, groupKey) => {
      const { cardList } = this.state;
      const { organizationId: userId } = this.props.auth;
      // Make copy to prevent mutating old list
      const safeCardList = [cardList];
      // Identify selected card by index
      const card = safeCardList[0];
      let newCard;
      if (card[groupKey] && card[groupKey].includes(filter.text)) {
        // Remove filter
        newCard = removeFilterFromComplexObject({
          complexObject: { ...card },
          groupKey,
          filter,
          targetProperty: 'text',
        });
      } else {
        // Add filter
        newCard = addFilterToComplexObject({
          complexObject: { ...card },
          groupKey,
          filter,
          targetProperty: 'text',
        });
      }

      safeCardList[0] = newCard;
      // Set state immediately for fast UI response
      this.setState({
        cardList: safeCardList[0],
      });
      // Send Mutation Request to Server here:
      // Convert display-formatted method values back to enum format for server
      const methodsForServer = newCard.method 
        ? newCard.method.map(m => displayToEnumFormat(m))
        : [];
      const response = await updateMatchSettings(
        {
          methods: methodsForServer,
          materials: newCard.material,
          minimumQuantity: newCard.minimumQuantity,
          minimumEstimatedCost: newCard.minimumEstimatedCost,
        },
        userId,
      );

      // Error handling
      if (response && response.errors) {
        console.error(errorMsgs.BidMatchSettings.editError);
      } else if (!response) {
        console.error(errorMsgs.BidMatchSettings.editError);
      }
    };

    _configureNotificationCards(cardList) {
      const normalizedCardList = Array.isArray(cardList) ? cardList : [cardList];
      return normalizedCardList.map(card => {
        const filterGroups = configureFilterGroups(this._onFilterClick);
        const {
          method = [],
          material = [],
          minimumQuantity = 0,
          minimumEstimatedCost = 0,
        } = card;
        
        // Check if all materials and methods are selected
        const allMaterialTexts = materialFilters.filters.map(f => f.text);
        const allMethodTexts = methodFilters.filters.map(f => f.text);
        const allMaterialsSelected = allMaterialTexts.length > 0 && 
          allMaterialTexts.every(m => material.includes(m)) && 
          material.length >= allMaterialTexts.length;
        const allMethodsSelected = allMethodTexts.length > 0 && 
          allMethodTexts.every(m => method.includes(m)) && 
          method.length >= allMethodTexts.length;
        const isAllSelected = allMaterialsSelected && allMethodsSelected;
        
        return {
          filterGroups,
          selectedFilters: {
            method,
            material,
          },
          minimumQuantity,
          minimumEstimatedCost,
          isAllSelected,
          onSelectAllClick: this._onSelectAllClick,
        };
      });
    }

    // _onAddCardClick = async () => {
    //   const { cardList } = this.state;
    //   const { id: userId } = this.props.auth;

    //   const emptyCard = {
    //     method: [],
    //     material: [],
    //     minimumQuantity: 0,
    //     minimumEstimatedCost: 0,
    //   };
    //   const newCardList = [...cardList, emptyCard];
    //   this.setState({ cardList: newCardList });

    //   // Make request
    //   const { errors } = await addMatchSettings(
    //     {
    //       materials: emptyCard.material,
    //       methods: emptyCard.method,
    //       minimumQuantity: emptyCard.minimumQuantity,
    //       minimumEstimatedCost: emptyCard.minimumEstimatedCost,
    //     },
    //     userId,
    //   );

    //   // Error handling
    //   if (errors) {
    //     console.error(errorMsgs.BidMatchSettings.addError);
    //   }
    // };

    // _onRemoveCardClick = async cardIndex => {
    //   const { cardList } = this.state;
    //   const { organizationId: userId } = this.props.auth;

    //   const newCardList = cardList.filter((card, idx) => cardIndex !== idx);
    //   this.setState({ cardList: newCardList });

    //   const { errors } = await deleteMatchSettings(cardIndex, userId);

    //   if (errors) {
    //     console.error(errorMsgs.BidMatchSettings.deleteError);
    //   }
    // };

    _onInputChange = async (rawInput, name) => {
      const input = parseFloat(rawInput);
      const { cardList } = this.state;
      const { organizationId: userId } = this.props.auth;
      const newMatchNotification = { ...cardList, [name]: input };
      this.setState({
        cardList: newMatchNotification,
      });
      this._updateMatchSettings(newMatchNotification, userId);
    };

    _updateMatchSettings = debounce(async (newMatchNotification, userId) => {
      // Convert display-formatted method values back to enum format for server
      const methodsForServer = newMatchNotification.method 
        ? newMatchNotification.method.map(m => displayToEnumFormat(m))
        : [];
      await updateMatchSettings(
        {
          methods: methodsForServer,
          materials: newMatchNotification.material,
          minimumQuantity: newMatchNotification.minimumQuantity,
          minimumEstimatedCost: newMatchNotification.minimumEstimatedCost,
        },
        userId,
      );
    }, 1000);

    _onSelectAllClick = async (shouldSelectAll) => {
      const { cardList } = this.state;
      const { organizationId: userId } = this.props.auth;
      const safeCardList = [cardList];
      const card = safeCardList[0];
      
      // Get all material and method texts
      const allMaterialTexts = materialFilters.filters.map(f => f.text);
      const allMethodTexts = methodFilters.filters.map(f => f.text);
      
      let newCard;
      if (shouldSelectAll) {
        // Select all materials and methods
        newCard = {
          ...card,
          material: allMaterialTexts,
          method: allMethodTexts,
        };
      } else {
        // Unselect all materials and methods
        newCard = {
          ...card,
          material: [],
          method: [],
        };
      }
      
      safeCardList[0] = newCard;
      // Set state immediately for fast UI response
      this.setState({
        cardList: safeCardList[0],
      });
      
      // Send Mutation Request to Server
      // Convert display-formatted method values back to enum format for server
      const methodsForServer = newCard.method 
        ? newCard.method.map(m => displayToEnumFormat(m))
        : [];
      const response = await updateMatchSettings(
        {
          methods: methodsForServer,
          materials: newCard.material,
          minimumQuantity: newCard.minimumQuantity,
          minimumEstimatedCost: newCard.minimumEstimatedCost,
        },
        userId,
      );
      
      // Error handling
      if (response && response.errors) {
        console.error(errorMsgs.BidMatchSettings.editError);
      } else if (!response) {
        console.error(errorMsgs.BidMatchSettings.editError);
      }
    };

    // This will be the new _updateMatchSettings

    // _altupdateMatchSettings = async () => {
    // await updatematchSettings(settings, userID)
    //   this.setState({
    //     uiMessage: { message: 'Match Settings updated!', type: 'success' },
    //   });
    // };

    render() {
      const { cardList, errors, isLoading, uiMessage } = this.state;

      // Configure Notification Cards
      const notificationCards = this._configureNotificationCards(cardList);

      // Configure Page
      const pageProps = {
        notificationCards,
        errors,
        isLoading,
        uiMessage,
        // onAddCardClick: this._onAddCardClick,
        // onRemoveCardClick: this._onRemoveCardClick,
        onInputChange: this._onInputChange,
        altupdateSettings: this._altupdateMatchSettings,
      };

      return <BidMatchSettingsPage {...pageProps} />;
    }
  }
  return WrappedComponent;
}



// WEBPACK FOOTER //
// ./src/components/BidMatchSettings/BidMatchSettingsHOC.jsx